import React, { useState, useEffect } from 'react';
import axios, { AxiosResponse } from 'axios';
import './TriggerEditor.css';

// These will be populated from the API
const AVAILABLE_OPERATORS = [
  { value: '>', label: 'Greater Than (>)' },
  { value: '>=', label: 'Greater Than or Equal (>=)' },
  { value: '=', label: 'Equal (=)' },
  { value: '!=', label: 'Not Equal (!=)' },
  { value: '<', label: 'Less Than (<)' },
  { value: '<=', label: 'Less Than or Equal (<=)' }
];

// Define available units
const AVAILABLE_UNITS = [
  { value: 'currency', label: 'Dollars ($)' },
  { value: 'percent', label: 'Percent (%)' },
  { value: 'times', label: 'Times' },
  { value: 'days', label: 'Days' }
];

// Define available aggregation types
const AVAILABLE_AGGREGATION_TYPES = [
  { value: 'none', label: 'None (Raw Value)' },
  { value: 'sum', label: 'Sum (Total)' },
  { value: 'average', label: 'Average' },
  { value: 'count', label: 'Count' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'once', label: 'Once (If It Happened)' },
  { value: 'first', label: 'First Time' },
  { value: 'avg', label: 'Average (Alias)' }
];

// Define available relationships
const AVAILABLE_RELATIONSHIPS = [
  { value: 'AND', label: 'AND (Both conditions must be true)' },
  { value: 'OR', label: 'OR (Either condition can be true)' }
];

// Define the structure of a trigger
interface Trigger {
  id?: number;
  name: string;
  action: string;
  detail?: string;
  operator: string;
  value: number | string;
  unit?: string;
  aggregation_type?: string;
  time_window?: number;
  group_by?: string;
  relationship?: 'AND' | 'OR';
  next_trigger_id?: number | null;
  trigger_once?: boolean;
}

// Event data structure
interface Event {
  id: number;
  user_id: string;
  action: string;
  detail: string;
  value: number;
  datetime: string;
  account_type: string;
  metadata: any;
}

// Interface for grouped action and details
interface ActionDetails {
  action: string;
  actionLabel: string; // For display
  details: string[];
}

interface TriggerEditorProps {
  triggerId?: number;
  trigger?: Trigger;
  programId?: number;
  onSaved: (trigger: Trigger) => void;
  onCancel: () => void;
  nextTriggerId?: number | null;
  relationship?: 'AND' | 'OR';
}

const TriggerEditor: React.FC<TriggerEditorProps> = ({
  triggerId,
  trigger,
  programId,
  onSaved,
  onCancel,
  nextTriggerId,
  relationship
}) => {
  const isEditing = !!triggerId;
  
  // State for available actions and details
  const [availableActions, setAvailableActions] = useState<ActionDetails[]>([]);
  const [loadingActions, setLoadingActions] = useState<boolean>(false);
  
  // State for the trigger data
  const [triggerData, setTriggerData] = useState<Trigger>({
    name: '',
    action: '',
    detail: '',
    operator: '>',
    value: 0,
    unit: 'currency',
    aggregation_type: 'none',
    time_window: 30,
    group_by: '',
    relationship: relationship || 'AND',
    next_trigger_id: nextTriggerId || null,
    trigger_once: false
  });
  
  // State for loading, error, and success
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Fetch available actions and details on component mount
  useEffect(() => {
    fetchAvailableActions();
  }, []);
  
  // Fetch trigger data on component mount if id is provided
  useEffect(() => {
    if (triggerId) {
      fetchTrigger();
    } else if (trigger) {
      setTriggerData({
        ...trigger,
        relationship: relationship || trigger.relationship || 'AND',
        next_trigger_id: nextTriggerId !== undefined ? nextTriggerId : trigger.next_trigger_id
      });
    }
  }, [triggerId, trigger, nextTriggerId, relationship]);
  
  // Function to fetch available actions and details from events
  const fetchAvailableActions = async () => {
    setLoadingActions(true);
    try {
      const response = await axios.get('/api/events');
      const events: Event[] = response.data;
      
      // Group events by action and collect unique details for each action
      const actionDetailsMap = new Map<string, Set<string>>();
      
      events.forEach(event => {
        if (!actionDetailsMap.has(event.action)) {
          actionDetailsMap.set(event.action, new Set<string>());
        }
        
        if (event.detail) {
          actionDetailsMap.get(event.action)?.add(event.detail);
        }
      });
      
      // Convert to array of ActionDetails
      const actionDetailsArray: ActionDetails[] = Array.from(actionDetailsMap.entries()).map(([action, detailsSet]) => {
        // Convert action to a more readable format (e.g., 'saved_money' -> 'Saved Money')
        const actionLabel = action
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        return {
          action,
          actionLabel,
          details: Array.from(detailsSet)
        };
      });
      
      setAvailableActions(actionDetailsArray);
      
      // Set initial action if we don't have one and there are actions available
      if (!triggerData.action && actionDetailsArray.length > 0) {
        setTriggerData(prev => ({
          ...prev,
          action: actionDetailsArray[0].action
        }));
      }
    } catch (err) {
      console.error('Error fetching available actions:', err);
      // Fallback to hardcoded actions if fetch fails
      setAvailableActions([
        { action: 'saved', actionLabel: 'Saved Money', details: ['checking', 'savings', 'investment'] },
        { action: 'created_goal', actionLabel: 'Created Goal', details: ['retirement', 'vacation', 'education', 'home'] },
        { action: 'talked_to', actionLabel: 'Talked To', details: ['advisor', 'customer service', 'partner'] },
        { action: 'joined', actionLabel: 'Joined', details: ['membership', 'program', 'webinar', 'newsletter'] },
        { action: 'completed', actionLabel: 'Completed', details: ['survey', 'onboarding', 'profile', 'training'] }
      ]);
    } finally {
      setLoadingActions(false);
    }
  };
  
  // Function to fetch trigger
  const fetchTrigger = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response: AxiosResponse<Trigger> = await axios.get(`/api/triggers/${triggerId}`);
      
      // If we're editing an existing trigger but want to change its relationship or next trigger
      const updatedData = {
        ...response.data,
        relationship: relationship !== undefined ? relationship : (response.data.relationship || 'AND'),
        next_trigger_id: nextTriggerId !== undefined ? nextTriggerId : response.data.next_trigger_id
      };
      
      setTriggerData(updatedData);
    } catch (err) {
      setError('Failed to load trigger. Please try again.');
      console.error('Error fetching trigger:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle name change
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTriggerData({
      ...triggerData,
      name: e.target.value
    });
  };
  
  // Handle action change
  const handleActionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAction = e.target.value;
    // Reset the detail when action changes
    setTriggerData({
      ...triggerData,
      action: newAction,
      detail: ''
    });
  };
  
  // Handle detail change
  const handleDetailChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTriggerData({
      ...triggerData,
      detail: e.target.value
    });
  };
  
  // Handle operator change
  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTriggerData({
      ...triggerData,
      operator: e.target.value
    });
  };
  
  // Handle value change
  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Allow both numeric and string values
    const numericValue = parseFloat(value);
    
    setTriggerData({
      ...triggerData,
      value: isNaN(numericValue) ? value : numericValue
    });
  };
  
  // Handle unit change
  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTriggerData({
      ...triggerData,
      unit: e.target.value
    });
  };
  
  // Handle aggregation type change
  const handleAggregationTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAggregationType = e.target.value;
    
    // If changing to an aggregation type that doesn't need time window, clear it
    const updatedData: Partial<Trigger> = {
      aggregation_type: newAggregationType
    };
    
    if (newAggregationType === 'none' || newAggregationType === 'once' || newAggregationType === 'first') {
      updatedData.time_window = undefined;
    }
    
    setTriggerData({
      ...triggerData,
      ...updatedData
    });
  };
  
  // Handle time window change
  const handleTimeWindowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    
    setTriggerData({
      ...triggerData,
      time_window: isNaN(value) ? undefined : value
    });
  };
  
  // Handle group by change
  const handleGroupByChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTriggerData({
      ...triggerData,
      group_by: e.target.value
    });
  };
  
  // Handle relationship change
  const handleRelationshipChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTriggerData({
      ...triggerData,
      relationship: e.target.value as 'AND' | 'OR'
    });
  };
  
  // Handle trigger_once toggle
  const handleTriggerOnceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTriggerData({
      ...triggerData,
      trigger_once: e.target.checked
    });
  };
  
  // Handle saving the trigger
  const handleSave = async () => {
    // Validate required fields
    if (!triggerData.name.trim()) {
      setError('Trigger name is required');
      return;
    }

    if (triggerData.value === '') {
      setError('Trigger value is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      let response: AxiosResponse<Trigger>;
      
      // Ensure program_id is included if provided
      const dataToSend = {
        ...triggerData,
        program_id: programId
      };
      
      if (isEditing && triggerId) {
        // Update existing trigger
        response = await axios.put(`/api/triggers/${triggerId}`, dataToSend);
      } else {
        // Create new trigger
        response = await axios.post('/api/triggers', dataToSend);
      }
      
      setSuccessMessage('Trigger saved successfully!');
      setTimeout(() => {
        onSaved(response.data);
      }, 1500);
    } catch (err) {
      setError('Failed to save trigger. Please try again.');
      console.error('Error saving trigger:', err);
    } finally {
      setSaving(false);
    }
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="trigger-editor-loading">
        <div className="spinner"></div>
        <p>Loading trigger data...</p>
      </div>
    );
  }
  
  // Helper function to determine if we should show the time window field
  const shouldShowTimeWindow = () => {
    const aggregationType = triggerData.aggregation_type;
    return aggregationType === 'sum' || 
           aggregationType === 'average' || 
           aggregationType === 'count' || 
           aggregationType === 'min' || 
           aggregationType === 'max' ||
           aggregationType === 'avg';
  };
  
  // Helper function to generate a preview of the trigger
  const generateTriggerPreview = () => {
    const currentAction = availableActions.find(a => a.action === triggerData.action);
    const action = currentAction?.actionLabel || triggerData.action;
    const operator = AVAILABLE_OPERATORS.find(o => o.value === triggerData.operator)?.label || triggerData.operator;
    const unit = triggerData.unit ? (AVAILABLE_UNITS.find(u => u.value === triggerData.unit)?.label || triggerData.unit) : '';
    const aggregation = triggerData.aggregation_type !== 'none' ? 
      (AVAILABLE_AGGREGATION_TYPES.find(a => a.value === triggerData.aggregation_type)?.label || triggerData.aggregation_type) : '';
    
    let preview = `When user ${action}`;
    
    if (triggerData.detail) {
      preview += ` (${triggerData.detail})`;
    }
    
    if (aggregation) {
      preview += ` with ${aggregation}`;
      
      if (shouldShowTimeWindow() && triggerData.time_window) {
        preview += ` over ${triggerData.time_window} days`;
      }
      
      if (triggerData.group_by) {
        preview += ` grouped by ${triggerData.group_by}`;
      }
    }
    
    preview += ` is ${operator} ${triggerData.value}`;
    
    if (unit && unit !== 'Times') {
      preview += ` ${unit}`;
    }
    
    return preview;
  };
  
  return (
    <div className="trigger-editor">
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      <div className="trigger-form">
        <div className="form-group">
          <label htmlFor="name">Trigger Name:</label>
          <input
            type="text"
            id="name"
            value={triggerData.name}
            onChange={handleNameChange}
            placeholder="Enter a descriptive name"
          />
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="action">Action:</label>
            {loadingActions ? (
              <div className="loading-select">Loading actions...</div>
            ) : (
              <select
                id="action"
                value={triggerData.action}
                onChange={handleActionChange}
                disabled={loadingActions}
              >
                <option value="" disabled>Select an action</option>
                {availableActions.map(actionDetail => (
                  <option key={actionDetail.action} value={actionDetail.action}>
                    {actionDetail.actionLabel}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="detail">Detail:</label>
            <select
              id="detail"
              value={triggerData.detail || ''}
              onChange={handleDetailChange}
              disabled={!triggerData.action || loadingActions}
            >
              <option value="">Select a detail</option>
              {triggerData.action && availableActions.find(a => a.action === triggerData.action)?.details.map(detail => (
                <option key={detail} value={detail}>
                  {detail}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="operator">Operator:</label>
            <select
              id="operator"
              value={triggerData.operator}
              onChange={handleOperatorChange}
            >
              {AVAILABLE_OPERATORS.map(operator => (
                <option key={operator.value} value={operator.value}>
                  {operator.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="value">Value:</label>
            <input
              type="text"
              id="value"
              value={triggerData.value.toString()}
              onChange={handleValueChange}
              placeholder="Target value"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="unit">Unit:</label>
            <select
              id="unit"
              value={triggerData.unit || ''}
              onChange={handleUnitChange}
            >
              {AVAILABLE_UNITS.map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="aggregation_type">Aggregation:</label>
            <select
              id="aggregation_type"
              value={triggerData.aggregation_type || 'none'}
              onChange={handleAggregationTypeChange}
            >
              {AVAILABLE_AGGREGATION_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          {shouldShowTimeWindow() && (
            <div className="form-group">
              <label htmlFor="time_window">Time Window (Days):</label>
              <input
                type="number"
                id="time_window"
                value={triggerData.time_window || ''}
                onChange={handleTimeWindowChange}
                placeholder="e.g., 30 days"
                min="1"
              />
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="group_by">Group By (Optional):</label>
            <input
              type="text"
              id="group_by"
              value={triggerData.group_by || ''}
              onChange={handleGroupByChange}
              placeholder="Field to group by"
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="relationship">Relationship to Next Trigger:</label>
          <select
            id="relationship"
            value={triggerData.relationship || 'AND'}
            onChange={handleRelationshipChange}
          >
            {AVAILABLE_RELATIONSHIPS.map(rel => (
              <option key={rel.value} value={rel.value}>
                {rel.label}
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-row trigger-behavior-row">
          <div className="form-group">
            <label className="checkbox-label" htmlFor="trigger_once">
              <input
                type="checkbox"
                id="trigger_once"
                checked={triggerData.trigger_once || false}
                onChange={handleTriggerOnceChange}
              />
              <span>Trigger only once per user</span>
            </label>
            <p className="help-text">
              When enabled, this trigger will only activate once for each user, even if they meet the conditions again in the future.
            </p>
          </div>
        </div>
        
        <div className="trigger-preview">
          <h3>Trigger Preview:</h3>
          <p>{generateTriggerPreview()}</p>
        </div>
        
        <div className="form-actions">
          <button className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Trigger' : 'Create Trigger')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TriggerEditor; 